import { Component, OnInit } from "@angular/core";
import { ComponentBase } from "../../models/ComponentBase";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faChevronCircleLeft } from "@fortawesome/free-solid-svg-icons";
import { Router } from "@angular/router";
import {
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
import { UpsertProjectCo2Request } from "../../WebApiClient";
import { ProjectCo2Service } from "../../services/projectco2.service";
import { ToastService } from "../../services/toast.service";
import { BlockNavigationIfChange } from "../../guards/block-onchanged.guard";
import { Observable } from "rxjs";

@Component({
    selector: "app-sustainability-projectco2-consumption",
    templateUrl: "./sustainability-projectco2-consumption.component.html",
    styleUrls: [
        "./sustainability-projectco2-consumption.component.scss",
        "../../style/sidebarLayout.scss",
        "../../style/form-table.scss",
    ],
    standalone: false,
})
export class SustainabilityProjectCo2ConsumptionComponent
    extends ComponentBase
    implements OnInit, BlockNavigationIfChange
{
    faBack: IconDefinition = faChevronCircleLeft;

    public projectCo2Consumption: UpsertProjectCo2Request;

    editCo2ConsumptionFormGroup: UntypedFormGroup = new UntypedFormGroup({
        portCrane: new UntypedFormControl(0, [Validators.required]),
        mobileCrane: new UntypedFormControl(0, [Validators.required]),
        vesselCrane: new UntypedFormControl(0, [Validators.required]),
        vesselPower: new UntypedFormControl(0, [Validators.required]),
        shorePower: new UntypedFormControl(0, [Validators.required]),
    });

    constructor(
        private readonly router: Router,
        private readonly projectCo2Service: ProjectCo2Service,
        private readonly toastService: ToastService
    ) {
        super();

        this.projectCo2Consumption = new UpsertProjectCo2Request();

        this.hasChanges = () => false;

        this.editCo2ConsumptionFormGroup.valueChanges.subscribe(() => {
            this.hasChanges = () => {
                return (
                    this.editCo2ConsumptionFormGroup.dirty &&
                    this.editCo2ConsumptionFormGroup.touched
                );
            };
        });
    }

    hasChanges: () => Observable<boolean> | Promise<boolean> | boolean;

    ngOnInit(): void {
        let projectCo2ConsumptionSubscription = this.projectCo2Service
            .getProjectCo2()
            .subscribe((c) => this.setProjectCo2Consumption(c));

        this.subscriptions.push(projectCo2ConsumptionSubscription);
    }

    private setProjectCo2Consumption(
        c: UpsertProjectCo2Request | undefined
    ): void {
        if (c === undefined) {
            return;
        }
        this.projectCo2Consumption.portCrane = c.portCrane;
        this.projectCo2Consumption.mobileCrane = c.mobileCrane;
        this.projectCo2Consumption.vesselCrane = c.vesselCrane;
        this.projectCo2Consumption.vesselPower = c.vesselPower;
        this.projectCo2Consumption.shorePower = c.shorePower;
        this.projectCo2Consumption.disclaimer = c.disclaimer;

        this.editCo2ConsumptionFormGroup.patchValue(this.projectCo2Consumption);
    }

    onSubmit() {
        if (!this.editCo2ConsumptionFormGroup.valid) {
            this.portCraneControl.markAsTouched();
            return;
        }

        this.hasChanges = () => false;

        this.projectCo2Consumption.portCrane = this.portCraneControl.value;
        this.projectCo2Consumption.mobileCrane = this.mobileCraneControl.value;
        this.projectCo2Consumption.vesselCrane = this.vesselCraneControl.value;
        this.projectCo2Consumption.vesselPower = this.vesselPowerControl.value;
        this.projectCo2Consumption.shorePower = this.shorePowerControl.value;

        this.projectCo2Service
            .upsert(this.projectCo2Consumption)
            .then(() =>
                this.toastService.add(
                    "Saved success",
                    "Co2 Consumption has been updated",
                    "success"
                )
            );

        this.editCo2ConsumptionFormGroup.markAsPristine();
    }

    back() {
        this.router.navigate(["/sustainability"]);
    }

    public get portCraneControl(): UntypedFormControl {
        return this.editCo2ConsumptionFormGroup.get(
            "portCrane"
        ) as UntypedFormControl;
    }

    public get mobileCraneControl(): UntypedFormControl {
        return this.editCo2ConsumptionFormGroup.get(
            "mobileCrane"
        ) as UntypedFormControl;
    }

    public get vesselCraneControl(): UntypedFormControl {
        return this.editCo2ConsumptionFormGroup.get(
            "vesselCrane"
        ) as UntypedFormControl;
    }

    public get vesselPowerControl(): UntypedFormControl {
        return this.editCo2ConsumptionFormGroup.get(
            "vesselPower"
        ) as UntypedFormControl;
    }

    public get shorePowerControl(): UntypedFormControl {
        return this.editCo2ConsumptionFormGroup.get(
            "shorePower"
        ) as UntypedFormControl;
    }
}
